/**
 * Mapbox GL JS web contract. One place for the web token, the Standard style
 * and the Standard configuration that native already owns.
 *
 * POR QUE EXISTE. La web montaba MapLibre con estilos MapTiler: otra libreria,
 * otro proveedor y otro lenguaje visual que el resto de la app. Mapbox GL JS
 * acepta exactamente la misma configuracion de Standard que usa el SDK nativo
 * (`mapboxStandardConfig`), asi que el mapa web y el de las apps no pueden
 * divergir: mismo estilo, misma camara, mismo contrato. `setConfig` reemplaza
 * la config entera del import `basemap`, igual que el `<StyleImport>` nativo.
 *
 * El token vive en `EXPO_PUBLIC_MAPBOX_TOKEN` (el mismo que ya usan los mapas
 * nativos). Sin token, la superficie muestra su fallback en vez de un canvas
 * en blanco.
 */
import mapboxgl from 'mapbox-gl'
import type { ConfigSpecification } from 'mapbox-gl'

import {
  MAPBOX_STANDARD_STYLE_URL,
  mapboxStandardConfig,
  type TagoMapPresentation,
  type TagoMapboxStandardStyleConfig,
} from './mapbox-standard'

export const MAPBOX_WEB_STYLE_URL = MAPBOX_STANDARD_STYLE_URL

export const MAPBOX_WEB_TOKEN = process.env.EXPO_PUBLIC_MAPBOX_TOKEN ?? ''

/** Mapbox reports its provider failures through these; retry copy depends on it. */
export const MAPBOX_WEB_NETWORK_ERROR = /Failed to fetch|NetworkError|Load failed|api\.mapbox\.com/i

/** Minimal structural type so the helper never depends on a wrapper's Map type. */
type ConfigurableMap = {
  setConfig: (importId: string, config: ConfigSpecification) => unknown
  getContainer?: () => HTMLElement | null | undefined
}

/**
 * Web parity for the native logo suppression.
 *
 * The native SDK has `logoEnabled={false}` / `attributionEnabled={false}` on
 * every map surface. GL JS has `attributionControl` but no logo switch: the
 * `LogoControl` is mounted unconditionally by the Map constructor. The product
 * shows no provider chrome on any map on any platform, so the mounted logo (and
 * any attribution node that survives a reworked prop) is removed from the DOM.
 * `sourcedata` only toggles `display` on the node — once the anchor is gone it
 * cannot come back.
 */
export function removeMapboxWebBranding(map: ConfigurableMap): void {
  const container = map.getContainer?.()
  if (!container || typeof container.querySelectorAll !== 'function') return
  for (const node of Array.from(
    container.querySelectorAll<HTMLElement>('.mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib'),
  )) {
    node.remove()
  }
}

let configured = false

/**
 * Configure the SDK once, before any <Map> mounts. The server-side render has
 * no window and deliberately does nothing.
 */
export function ensureMapboxWeb(): void {
  if (configured || typeof window === 'undefined') return

  mapboxgl.accessToken = MAPBOX_WEB_TOKEN
  configured = true
}

export function hasMapboxWebToken(): boolean {
  return Boolean(MAPBOX_WEB_TOKEN)
}

export function mapboxWebConfig(
  presentation: TagoMapPresentation,
  isDark: boolean,
): TagoMapboxStandardStyleConfig {
  return mapboxStandardConfig(presentation, isDark ? 'dark' : 'light')
}

/** Apply the app's Standard configuration to a mounted Mapbox map. */
export function applyMapboxWebConfig(
  map: ConfigurableMap,
  presentation: TagoMapPresentation,
  isDark: boolean,
): void {
  map.setConfig('basemap', mapboxWebConfig(presentation, isDark))
  removeMapboxWebBranding(map)
}
